import React, { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  formatAmountField,
  formatDateWithDayjs,
  formatNumber,
  showAlert,
  showToast,
  capitalizeFirstLetter,
  showPartPaymentGoCardlessNotice,
  partPaymentGoCardlessWarningOf
} from "../../../../../utils/utils";
import {
  useTable,
  useRowSelect,
  useFilters,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import { COLUMNS } from "./Columns";
import { Checkbox } from "../../../../../components/common/Checkbox";
import { Link } from "react-router-dom";
import {
  alertActions,
  fundedInvoicesData,
  noDataPresent,
  repaymentDateAlert
} from "../../../../../locales/en";
import "./paymentreporttable.scss";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../../hooks/useVisibleColumnCount";
import {
  CURRENCY_FORMAT,
  REPORT_PAGE_SIZE,
  REPORT_PAGE_SIZE_OPTIONS,
  showToastType
} from "../../../../../utils/constants/appConstants";
import { CLIENT_DETAILS, INVOICE_DETAILS } from "../../../../../route/paths";
import PaymentReportDatePicker from "../../../../datepicker/PaymentReportDatePicker";
import privateAxios from "../../../../../utils/axiosPrivate";
import {
  getPostDateUrl,
  getRepaymentDateChangeUrl
} from "../../../../../utils/constants/apiEndPoints";
import DualPagination from "../../../../common/DualPagination";
import { useDispatch } from "react-redux";
import {
  setSelectedReportData,
  setShowTopHideFilter
} from "../../../../../redux/userReportsSlice";

const SelectionHeader = ({ getToggleAllRowsSelectedProps }) => (
  <Checkbox {...getToggleAllRowsSelectedProps()} className="headercheckBox" />
);
SelectionHeader.propTypes = {
  getToggleAllRowsSelectedProps: PropTypes.func.isRequired
};

const SelectionCellWrapper = ({ row }) => (
  <Checkbox {...row.getToggleRowSelectedProps()} className="bodycheckBox" />
);
SelectionCellWrapper.propTypes = {
  row: PropTypes.object.isRequired
};

const PaymentReportTable = ({
  data,
  loading,
  totalPages = 0,
  pageIndex = 0,
  pageSize = REPORT_PAGE_SIZE,
  setPageIndex,
  setPageSize
}) => {
  const dispatch = useDispatch();
  const baseColumns = useMemo(() => COLUMNS, []);
  const [expandedRows, setExpandedRows] = useState([]);
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const SelectionCell = useCallback(
    ({ row }) => <SelectionCellWrapper row={row} />,
    [SelectionCellWrapper]
  );

  const tableColumns = useMemo(() => {
    return [
      {
        id: "selection",
        Header: SelectionHeader,
        Cell: SelectionCell
      },
      ...baseColumns
    ];
  }, [data, baseColumns, SelectionCell]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    selectedFlatRows
  } = useTable(
    {
      columns: tableColumns,
      data: data || [],
      initialState: { pageIndex, pageSize },
      manualPagination: true,
      autoResetPage: false,
      autoResetSelectedRows: false
    },
    useFilters,
    useGlobalFilter,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const toggleRow = (rowId, event) => {
    event.stopPropagation();
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  };

  // Reayment Date Change Function Start
  const [showNewDateButton, setShowNewDateButton] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [rowDates, setRowDates] = useState({});
  const handleRepayDate = (date, rowId) => {
    const formattedDate = date ? date.toLocaleDateString("en-CA") : "";
    setRowDates((prev) => ({
      ...prev,
      [rowId]: date
    }));

    setSelectedQuoteId(rowId);
    setSelectedDate(formattedDate);

    setShowNewDateButton((prev) => ({
      ...prev,
      [rowId]: true
    }));
  };

  const handleNewDateButtonClick = async () => {
    if (!selectedQuoteId || !selectedDate) return;
    const result = await showAlert(
      repaymentDateAlert?.title,
      repaymentDateAlert?.payReportSubtitle,
      alertActions?.warning,
      alertActions?.Confirm,
      alertActions?.Cancel
    );

    if (!result.isConfirmed) return;

    try {
      // Asked before the save, so staff can still cancel and leave the date as it is.
      const preview = await privateAxios.post(getPostDateUrl, {
        quoteIds: [selectedQuoteId],
        repaymentDate: selectedDate
      });
      const previewWarning = partPaymentGoCardlessWarningOf(preview);
      if (previewWarning) {
        const acknowledged =
          await showPartPaymentGoCardlessNotice(previewWarning);
        if (!acknowledged.isConfirmed) return;
      }

      const dataToSend = {
        quoteId: selectedQuoteId,
        repayDate: selectedDate
      };

      const response = await privateAxios.put(getRepaymentDateChangeUrl, null, {
        params: dataToSend
      });

      // This endpoint reports a refused or failed save as { success: false } under a
      // 200, for example a collection submitted to GoCardless after the preview. The
      // date did not change, so the picked date stays for staff to see.
      const saveResult = response?.data?.data;
      if (saveResult?.success === false) {
        showToast(saveResult.error, showToastType?.error);
        return;
      }

      if (response.status === 200) {
        showToast(response.data.message, showToastType?.success);
        // A part payment recorded after the preview is reported only by the save,
        // and by then the date has moved, so this notice has no Cancel.
        const saveWarning = partPaymentGoCardlessWarningOf(response);
        if (saveWarning && !previewWarning) {
          showPartPaymentGoCardlessNotice(saveWarning, { allowCancel: false });
        }
        setShowNewDateButton(false);
        setSelectedQuoteId(null);
        setSelectedDate(null);
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType?.error);
    }
  };

  const dateFields = useMemo(() => ["repayment_date", "payment_date"], []);
  const payFields = useMemo(
    () => ["totalowing", "owing_repaydate", "owingToday"],
    []
  );

  const renderFormattedValue = useCallback(
    (column, row) => {
      const value = row.original[column.accessor];

      if (dateFields.includes(column.accessor)) {
        return formatDateWithDayjs(value, "DD-MMM-YYYY");
      }

      if (column.accessor === "FT_no") {
        return (
          <Link to={INVOICE_DETAILS(row.original.fund_quote_id)}>{value}</Link>
        );
      }

      if (column.accessor === "legal_name") {
        return (
          <Link to={CLIENT_DETAILS(row.original.user_id)}>
            {value?.trim() || row.original.customer_name}
          </Link>
        );
      }

      if (payFields.includes(column.accessor)) {
        if (value === null || value === undefined || value === "") return "";
        const num = Number(value);
        if (isNaN(num)) return String(value);
        return `${CURRENCY_FORMAT}${formatNumber(num)}`;
      }

      if (
        column.accessor === "xero_status" ||
        column.accessor === "gocardless_status"
      ) {
        return capitalizeFirstLetter(value);
      }

      return String(value ?? "");
    },
    [dateFields, payFields]
  );

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;
  const pageCount = totalPages;
  const pageOptions = useMemo(
    () => [...Array(totalPages).keys()],
    [totalPages]
  );

  const gotoPage = (updater) => {
    setPageIndex(updater);
  };

  const previousPage = () => {
    const newPage = Math.max(0, pageIndex - 1);
    setPageIndex(newPage);
  };

  const nextPage = () => {
    const newPage = pageIndex < totalPages - 1 ? pageIndex + 1 : pageIndex;
    setPageIndex(newPage);
  };

  useEffect(() => {
    dispatch(setShowTopHideFilter(selectedFlatRows.length > 0));
  }, [selectedFlatRows.length, dispatch]);

  // Group Status Change Function Start
  const safeRows = selectedFlatRows ?? [];

  const quoteIds = useMemo(
    () => safeRows.map((row) => row.original?.FT_no).filter(Boolean),
    [safeRows]
  );

  const paymentIds = useMemo(
    () => safeRows.map((row) => row.original?.payment_id).filter(Boolean),
    [safeRows]
  );

  const selectedUserAccountIds = useMemo(
    () => safeRows.map((row) => row.original?.user_id).filter(Boolean),
    [safeRows]
  );

  const userQuoteIdsSelected = useMemo(
    () => [...new Set(quoteIds)],
    [quoteIds]
  );

  const selectedPayload = useMemo(
    () => ({
      quoteIds,
      paymentIds,
      selectedUserAccountIds,
      userQuoteIdsSelected
    }),
    [quoteIds, paymentIds, selectedUserAccountIds, userQuoteIdsSelected]
  );

  useEffect(() => {
    if (selectedFlatRows.length > 0) {
      dispatch(setSelectedReportData(selectedPayload));
    }
  }, [selectedFlatRows.length, selectedPayload, dispatch]);

  const renderCellContent = (cell, row) => {
    if (cell.column.id === "repayment_date") {
      return (
        <>
          <PaymentReportDatePicker
            selected={
              rowDates[row.original.fund_quote_id] || new Date(cell.value)
            }
            onChange={(date) =>
              handleRepayDate(date, row.original.fund_quote_id)
            }
          />

          {showNewDateButton[row.original.fund_quote_id] && (
            <button
              className="btn btn-primary new-pay-date btn-sm mt-1"
              onClick={handleNewDateButtonClick}
            >
              {fundedInvoicesData?.funded?.newDate}
            </button>
          )}
        </>
      );
    }

    if (cell.column.id === "legal_name") {
      return (
        <Link to={CLIENT_DETAILS(row.original.user_id)}>
          {cell.value?.trim() || row.original.customer_name}
        </Link>
      );
    }

    if (cell.column.id === "FT_no") {
      return (
        <Link to={INVOICE_DETAILS(row.original.fund_quote_id)}>
          FT{String(cell.value || "").replace(/^FT/, "")}
        </Link>
      );
    }

    if (cell.column.id === "amount") {
      const formatted = formatAmountField(cell.value);
      if (formatted?.type === "html") {
        return (
          <span style={{ color: formatted.color }}>{formatted.value}</span>
        );
      }
      return formatted?.value ?? "";
    }

    return cell.render("Cell");
  };

  return (
    <>
      <table
        className="mt-2 mb-3 paymentreport-table-wrap bgless_table"
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                <th />
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} key={column.id}>
                    <span>{column.render("Header")}</span>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>

        <tbody {...getTableBodyProps()}>
          {loading && <TableSkeleton rows={10} columns={visibleColumnCount} />}
          {!loading &&
            page.filter((row) => !row.original.deletedAt).length === 0 && (
              <tr>
                <td colSpan={baseColumns.length + 2} className="text-center">
                  {noDataPresent}
                </td>
              </tr>
            )}
          {!loading &&
            page.filter((row) => !row.original.deletedAt).length > 0 &&
            page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              const isSelected = row.isSelected;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={`parentrow ${isSelected ? "selected" : ""}`}
                    {...row.getRowProps()}
                  >
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={(e) => toggleRow(row.id, e)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>

                    {row.cells.map((cell) => (
                      <td key={cell.column.id} {...cell.getCellProps()}>
                        {renderCellContent(cell, row)}
                      </td>
                    ))}
                  </tr>

                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={baseColumns.length + 2}>
                        <div className="tdbody">
                          {baseColumns.map((column) => (
                            <span
                              key={column.accessor}
                              data-label={column.Header}
                            >
                              {renderFormattedValue(column, row)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>

      {data && data.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
          pageSizeOptions={REPORT_PAGE_SIZE_OPTIONS}
        />
      )}
    </>
  );
};

PaymentReportTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  totalPages: PropTypes.number,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  setPageIndex: PropTypes.func,
  setPageSize: PropTypes.func
};

export default PaymentReportTable;
